import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import Keycloak from 'keycloak-js';
import { ToastrService } from 'ngx-toastr';
import { finalize, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AgenciesService } from '../../../orval-gen/endpoints/agencies/agencies.service';
import { AgencyRegisterBody } from '../../../orval-gen/models';
import { skipErrorToast } from '../../interceptors/error.interceptor';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ErrorDirective } from '../../shared/directives/error.directive';
import { HelpersService } from '../../shared/services/helpers.service';
import { unwrap } from '../../utils/http-utils';

@Component({
  selector: 'app-register',
  imports: [ReactiveFormsModule, ErrorDirective, RouterLink, IconComponent],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit {
  accountForm!: FormGroup;
  registerAvailable = environment.registerAvailable;
  submitting = false;

  constructor(
    private agenciesService: AgenciesService,
    private toastr: ToastrService,
    private router: Router,
    private keycloak: Keycloak,
    private fb: FormBuilder,
    private helpersService: HelpersService
  ) {}

  ngOnInit() {
    if (!this.registerAvailable) {
      this.router.navigate(['/']);
    } else if (this.keycloak.authenticated) {
      this.router.navigate(['/dashboard']);
    }
    this.buildAccountForm();
  }

  buildAccountForm(): void {
    this.accountForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      // A preview of the name the hub gives the agency; it is not sent.
      agency: [{ value: '', disabled: true }],
      company_name: ['', Validators.required],
      street: ['', Validators.required],
      postal_code: ['', Validators.required],
      phone_contact: ['', Validators.required],
    });
  }

  /** Previews the agency name the hub derives: the domain's first label, letters and digits only. */
  onEmailBlur(event: FocusEvent): void {
    const domain = (event.target as HTMLInputElement).value.split('@')[1] ?? '';
    const agency = domain
      .split('.')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    this.accountForm.patchValue({ agency });
  }

  login(): void {
    this.keycloak.login({ redirectUri: window.location.origin });
  }

  saveChanges(): void {
    this.submitting = true;
    const { agency: _, ...body }: AgencyRegisterBody & { agency: string } =
      this.accountForm.getRawValue();

    this.agenciesService
      .agencyRegister(body, { context: skipErrorToast() })
      .pipe(
        unwrap(),
        finalize(() => {
          this.submitting = false;
        })
      )
      .subscribe({
        next: registration => {
          this.toastr.success(
            `${registration.agency.name} has been registered. Check ${registration.agent.email} for the invitation to set up your account.`,
            'Success'
          );
          timer(2000).subscribe(() => this.router.navigate(['/']));
        },
        error: err => {
          if (this.helpersService.handleApiErrors(this.accountForm, err?.error)) {
            this.toastr.error('Please correct the highlighted fields.', 'Validation Error');
          } else {
            // An address already registered, a taken name or a full consumer, each a 409 with its reason.
            this.toastr.error(err?.error?.detail ?? 'Failed to register agency', 'Error');
          }
          console.error('Agency registration error:', err);
        },
      });
  }
}
